import { useEffect } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
import { Image } from '@tiptap/extension-image'
import {
  BetweenHorizontalEnd,
  BetweenVerticalEnd,
  Bold,
  Heading,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Table,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils/cn'
import './sop-document.css'

export interface SopSectionEditorProps {
  value: string
  onChange: (html: string) => void
  disabled?: boolean
  label: string
}

/**
 * Editor for one SOP template section. Unlike the shared RichTextEditor it
 * keeps tables, images and sub-headings: the SOPs imported from Odoo
 * Knowledge are full of them, and an editor without those node types would
 * silently delete them on the first keystroke. Edits in the same document
 * styles the reader uses (`.sop-doc`).
 */
export function SopSectionEditor({ value, onChange, disabled = false, label }: SopSectionEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [3, 4] }, link: { openOnClick: false } }),
      TableKit.configure({ table: { resizable: false } }),
      Image.configure({ allowBase64: true }),
    ],
    content: value,
    editable: !disabled,
    // Only edits made by the person: TipTap also fires on load when it
    // normalises imported tables, and that must not autosave anything.
    onUpdate: ({ editor: e }) => {
      if (e.isFocused) onChange(e.isEmpty ? '' : e.getHTML())
    },
    editorProps: { attributes: { class: 'sop-doc', 'aria-label': label, role: 'textbox', 'aria-multiline': 'true' } },
  })

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [editor, disabled])

  // The server copy changed while this section wasn't being edited
  // (another tab, a conversion run): show it. Never while focused.
  useEffect(() => {
    if (!editor || editor.isFocused) return
    const current = editor.isEmpty ? '' : editor.getHTML()
    if (current !== value) editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, value])

  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-border-default bg-surface focus-within:border-border-focus">
      {editor ? <Toolbar editor={editor} disabled={disabled} /> : null}
      <EditorContent editor={editor} className="max-h-[70vh] overflow-auto px-5 py-4" />
    </div>
  )
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const inTable = editor.isActive('table')
  const btn = (label: string, Icon: typeof Bold, active: boolean, run: () => void) => (
    <Button
      key={label}
      type="button"
      variant="ghost"
      size="sm"
      iconOnly
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={cn(active && 'bg-surface-active text-primary')}
      onClick={run}
    >
      <Icon aria-hidden="true" size={16} />
    </Button>
  )
  const chain = () => editor.chain().focus()

  const toggleLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', prev ?? 'https://')
    if (url === null) return
    if (url === '') chain().extendMarkRange('link').unsetLink().run()
    else chain().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-border-subtle bg-surface-sunk px-2 py-1">
      {btn('Sub-heading', Heading, editor.isActive('heading', { level: 3 }), () => chain().toggleHeading({ level: 3 }).run())}
      {btn('Bold', Bold, editor.isActive('bold'), () => chain().toggleBold().run())}
      {btn('Italic', Italic, editor.isActive('italic'), () => chain().toggleItalic().run())}
      <span className="mx-1 h-5 w-px bg-border-default" aria-hidden="true" />
      {btn('Bulleted list', List, editor.isActive('bulletList'), () => chain().toggleBulletList().run())}
      {btn('Numbered steps', ListOrdered, editor.isActive('orderedList'), () => chain().toggleOrderedList().run())}
      {btn('Tip or note', Quote, editor.isActive('blockquote'), () => chain().toggleBlockquote().run())}
      {btn('Link', LinkIcon, editor.isActive('link'), toggleLink)}
      <span className="mx-1 h-5 w-px bg-border-default" aria-hidden="true" />
      {btn('Insert table', Table, false, () => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}
      {inTable ? (
        <>
          {btn('Add row', BetweenHorizontalEnd, false, () => chain().addRowAfter().run())}
          {btn('Add column', BetweenVerticalEnd, false, () => chain().addColumnAfter().run())}
          {btn('Delete table', Trash2, false, () => chain().deleteTable().run())}
        </>
      ) : null}
    </div>
  )
}
